import { toast } from 'react-toastify';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';
import { getCohortAssignmentSearchStr } from '../utils/common';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IClientAssignment, ICohort } from '../utils/types';

const name = 'cohorts';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<CohortState>) => createExtraReducers(builder),
});

export interface CohortState {
  list: Array<ICohort>;
  error: any;
  loading: boolean;
  current: ICohort;
  cohortServiceClasses: Array<string>;
  clientCohortAssignments: Array<IClientAssignment>;
}

function createInitialState(): CohortState {
  return {
    list: [],
    loading: false,
    error: null,
    current: { name: '', service_classes: [], client_assignments: [] },
    cohortServiceClasses: [],
    clientCohortAssignments: [],
  };
}

function createReducers() {
  return {
    clearClientCohortState,
  };

  function clearClientCohortState(state: CohortState) {
    state.list = [];
    state.error = null;
    state.current = { name: '', service_classes: [], client_assignments: [] };
    state.cohortServiceClasses = [];
    state.clientCohortAssignments = [];
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/cohorts`;

  return {
    getList: getList(),
    createCohort: createCohort(),
    updateCohort: updateCohort(),
    deleteCohort: deleteCohort(),
    getCohortServiceClasses: getCohortServiceClasses(),
    getCohortById: getCohortById(),

    // Client Cohort Assignments
    getClientCohortAssignments: getClientCohortAssignments(),
    createClientCohortAssignment: createClientCohortAssignment(),
    updateClientCohortAssignment: updateClientCohortAssignment(),
    deleteClientCohortAssignment: deleteClientCohortAssignment(),
  };

  function getList() {
    return createAsyncThunk(`${name}/getList`, async ({ getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.get({
        url: `${baseUrl}/`,
        getAccessTokenSilently,
      });
      return response;
    });
  }

  function createCohort() {
    return createAsyncThunk(
      `${name}/createCohort`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateCohort() {
    return createAsyncThunk(
      `${name}/updateCohort`,
      async ({ cohortId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          { url: `${baseUrl}/${cohortId}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function deleteCohort() {
    return createAsyncThunk(
      `${name}/deleteCohort`,
      async ({ cohortId, forceDelete = false, getAccessTokenSilently }: any) => {
        const url = `${baseUrl}/${cohortId}/?force=${forceDelete}`;
        const response = await fetchWrapper.delete({
          url: url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getCohortServiceClasses() {
    return createAsyncThunk(
      `${name}/getCohortServiceClasses`,
      async ({ getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/service_classes/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getCohortById() {
    return createAsyncThunk(
      `${name}/getCohortById`,
      async ({ cohortId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${cohortId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  // Client Cohort Assignments
  function getClientCohortAssignments() {
    return createAsyncThunk(
      `${name}/getClientCohortAssignments`,
      async ({ params, getAccessTokenSilently }: any) => {
        let baseSearch = `${API_ENDPOINT}/client_cohort_assignments/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getCohortAssignmentSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getCohortAssignmentSearchStr(key, params[key])}`;
            }
          });
        }
        const response = await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently });
        return response;
      },
    );
  }

  function createClientCohortAssignment() {
    return createAsyncThunk(
      `${name}/createClientCohortAssignment`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${API_ENDPOINT}/client_cohort_assignments/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateClientCohortAssignment() {
    return createAsyncThunk(
      `${name}/updateClientCohortAssignment`,
      async ({ assignmentId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${API_ENDPOINT}/client_cohort_assignments/${assignmentId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteClientCohortAssignment() {
    return createAsyncThunk(
      `${name}/deleteClientCohortAssignment`,
      async ({ assignmentId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${API_ENDPOINT}/client_cohort_assignments/${assignmentId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<CohortState>) {
  const {
    getList,
    createCohort,
    updateCohort,
    deleteCohort,
    getCohortServiceClasses,
    getCohortById,
    getClientCohortAssignments,
    createClientCohortAssignment,
    updateClientCohortAssignment,
    deleteClientCohortAssignment,
  } = extraActions;

  builder
    //GET Cohorts
    .addCase(getList.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: CohortState, action: PayloadAction<ICohort[]>) => {
      state.list = action.payload;
      state.loading = false;
    })
    .addCase(getList.rejected, (state: CohortState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
    })

    // Create Cohort
    .addCase(createCohort.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createCohort.fulfilled, (state: CohortState, _action: PayloadAction<ICohort>) => {
      state.loading = false;
      toast.success('New cohort created successfully');
    })
    .addCase(createCohort.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // Update Cohort
    .addCase(updateCohort.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateCohort.fulfilled, (state: CohortState, _action: PayloadAction<ICohort>) => {
      state.loading = false;
      toast.success('Cohort updated successfully');
    })
    .addCase(updateCohort.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // Delete Cohort
    .addCase(deleteCohort.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteCohort.fulfilled, (state: CohortState, _action: PayloadAction<ICohort>) => {
      state.loading = false;
      toast.success('Cohort deleted successfully');
    })
    .addCase(deleteCohort.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // Get Cohort Service Classes
    .addCase(getCohortServiceClasses.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getCohortServiceClasses.fulfilled,
      (state: CohortState, action: PayloadAction<Array<string>>) => {
        state.cohortServiceClasses = action.payload;
        state.loading = false;
      },
    )
    .addCase(getCohortServiceClasses.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // Get Cohort By Id
    .addCase(getCohortById.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getCohortById.fulfilled, (state: CohortState, action: PayloadAction<ICohort>) => {
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(getCohortById.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    /*** Client Cohort Assignments ***/
    // Get Client Cohort Assignments
    .addCase(getClientCohortAssignments.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getClientCohortAssignments.fulfilled,
      (state: CohortState, action: PayloadAction<Array<any>>) => {
        state.clientCohortAssignments = action.payload;
        state.loading = false;
      },
    )
    .addCase(getClientCohortAssignments.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // Create Client Cohort Assignment
    .addCase(createClientCohortAssignment.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createClientCohortAssignment.fulfilled,
      (state: CohortState, _action: PayloadAction<any>) => {
        state.loading = false;
        toast.success('Client cohort assignment created successfully');
      },
    )
    .addCase(createClientCohortAssignment.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // Update Client Cohort Assignment
    .addCase(updateClientCohortAssignment.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateClientCohortAssignment.fulfilled,
      (state: CohortState, _action: PayloadAction<any>) => {
        state.loading = false;
        toast.success('Client cohort assignment updated successfully');
      },
    )
    .addCase(updateClientCohortAssignment.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // Delete Client Cohort Assignment
    .addCase(deleteClientCohortAssignment.pending, (state: CohortState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteClientCohortAssignment.fulfilled,
      (state: CohortState, _action: PayloadAction<any>) => {
        state.loading = false;
        toast.success('Client cohort assignment deleted successfully');
      },
    )
    .addCase(deleteClientCohortAssignment.rejected, (state: CohortState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const cohortActions = { ...slice.actions, ...extraActions };
export const cohortReducer = slice.reducer;
